import { useState, useEffect } from 'react';
import { useLoyalty } from '../../../contexts/LoyaltyContext';
import { useAuth } from '../../../contexts/AuthContext';
import { apiGet, apiPost } from '../../../lib/api';

// ─── Types ───────────────────────────────────────────────────────────────────

interface PollOption {
  id: string;
  text: string;
  sortOrder: number;
}

interface PollQuestion {
  id: string;
  text: string;
  type: 'single_choice' | 'multiple_choice';
  options: PollOption[];
}

interface Poll {
  id: string;
  title: string;
  description?: string | null;
  questions: PollQuestion[];
  completionPoints?: number | null;
  hasResponded?: boolean;
}

interface PollResultOption {
  id: string;
  text: string;
  count: number;
  percent: number;
}

interface PollResults {
  totalResponses: number;
  questions: Array<{
    id: string;
    text: string;
    options: PollResultOption[];
  }>;
}

interface RespondResult {
  pointsAwarded?: number | null;
}

interface LoyaltyPollBlockProps {
  questionnaireId?: string;
  programId?: string;
}

// ─── Spinner ─────────────────────────────────────────────────────────────────

function Spinner() {
  return (
    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
    </svg>
  );
}

// ─── Results bar chart ────────────────────────────────────────────────────────

function ResultsView({ results }: { results: PollResults }) {
  return (
    <div className="space-y-5">
      {results.questions.map((q) => (
        <div key={q.id}>
          <p className="text-sm font-semibold text-[var(--color-text,#e2e8f0)] mb-3">{q.text}</p>
          <div className="space-y-2">
            {q.options.map((opt) => (
              <div key={opt.id}>
                <div className="flex justify-between text-xs text-[var(--color-text-muted,#94a3b8)] mb-1">
                  <span>{opt.text}</span>
                  <span className="font-medium">{opt.percent}%</span>
                </div>
                <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-[var(--color-primary,#6366f1)] transition-all duration-700"
                    style={{ width: `${opt.percent}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      <p className="text-xs text-[var(--color-text-muted,#94a3b8)]">
        {results.totalResponses} vote{results.totalResponses !== 1 ? 's' : ''} total
      </p>
    </div>
  );
}

// ─── Block ────────────────────────────────────────────────────────────────────

export function LoyaltyPollBlock({ props }: { props: LoyaltyPollBlockProps }) {
  const { questionnaireId } = props;
  const { isAuthenticated } = useAuth();
  const { data: loyaltyData, refetch } = useLoyalty();

  const [poll, setPoll] = useState<Poll | null>(null);
  const [results, setResults] = useState<PollResults | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [voted, setVoted] = useState(false);
  const [pointsFeedback, setPointsFeedback] = useState<number | null>(null);

  const [answers, setAnswers] = useState<Record<string, string[]>>({});

  useEffect(() => {
    if (!questionnaireId || !isAuthenticated) {
      setLoading(false);
      return;
    }
    setLoading(true);
    apiGet<Poll>(`/customer/questionnaires/${questionnaireId}`)
      .then((data) => {
        setPoll(data);
        if (data.hasResponded) {
          setVoted(true);
          return apiGet<PollResults>(`/customer/questionnaires/${questionnaireId}/results`);
        }
        return null;
      })
      .then((res) => {
        if (res) setResults(res);
      })
      .catch((err) => {
        setError((err as Error).message ?? 'Failed to load poll');
      })
      .finally(() => setLoading(false));
  }, [questionnaireId, isAuthenticated]);

  function toggleAnswer(questionId: string, optionId: string, multi: boolean) {
    setAnswers((prev) => {
      const current = prev[questionId] ?? [];
      if (multi) {
        return {
          ...prev,
          [questionId]: current.includes(optionId)
            ? current.filter((id) => id !== optionId)
            : [...current, optionId],
        };
      }
      return { ...prev, [questionId]: [optionId] };
    });
  }

  async function handleVote() {
    if (!poll) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        answers: Object.entries(answers).map(([questionId, optionIds]) => ({
          questionId,
          optionIds,
        })),
      };

      const result = await apiPost<RespondResult>(
        `/customer/questionnaires/${questionnaireId}/respond`,
        payload,
      );

      const pts = result.pointsAwarded ?? poll.completionPoints ?? null;
      if (pts != null && pts > 0) {
        setPointsFeedback(pts);
        setTimeout(() => setPointsFeedback(null), 4000);
      }

      const res = await apiGet<PollResults>(`/customer/questionnaires/${questionnaireId}/results`);
      setResults(res);
      setVoted(true);
      refetch();
    } catch (err) {
      const errData = (err as Error & { data?: { error?: string } }).data;
      setError(errData?.error ?? (err instanceof Error ? err.message : 'Failed to submit vote'));
    } finally {
      setSubmitting(false);
    }
  }

  // ── Not authenticated ──────────────────────────────────────────────────────

  if (!isAuthenticated) {
    return (
      <div className="loyalty-card p-6 text-center">
        <span className="text-3xl mb-3 block">📊</span>
        <p className="text-sm text-[var(--color-text-muted,#94a3b8)] mb-3">
          Log in to vote and earn points.
        </p>
        <a
          href="/login"
          className="inline-block px-5 py-2 rounded-lg bg-[var(--color-primary,#6366f1)] text-white text-sm font-semibold hover:opacity-90 transition-opacity"
        >
          Log in to vote
        </a>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loyalty-card p-8 flex justify-center">
        <svg className="w-5 h-5 animate-spin text-[var(--color-primary,#6366f1)]" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
        </svg>
      </div>
    );
  }

  if (error && !poll) {
    return (
      <div className="loyalty-card p-4">
        <p className="text-sm text-red-400">{error}</p>
      </div>
    );
  }

  if (!poll) return null;

  return (
    <div className="loyalty-card p-6 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="loyalty-heading text-sm font-bold text-[var(--color-text,#e2e8f0)]">
            {poll.title}
          </h3>
          {poll.description && (
            <p className="text-xs text-[var(--color-text-muted,#94a3b8)] mt-0.5">{poll.description}</p>
          )}
        </div>
        {poll.completionPoints != null && poll.completionPoints > 0 && (
          <span className="flex-shrink-0 text-xs font-semibold text-yellow-400 bg-yellow-400/10 border border-yellow-400/20 rounded-full px-2.5 py-0.5">
            +{poll.completionPoints} pts
          </span>
        )}
      </div>

      {/* Points feedback */}
      {pointsFeedback != null && (
        <div className="flex items-center gap-2 text-sm font-semibold text-yellow-400 bg-yellow-400/10 border border-yellow-400/20 rounded-lg px-4 py-2">
          <span>⭐</span>
          <span>+{pointsFeedback} points earned!</span>
        </div>
      )}

      {/* Already voted — show results */}
      {voted && results ? (
        <>
          <div className="flex items-center gap-1.5 text-xs font-medium text-green-400">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            Already voted
          </div>
          <ResultsView results={results} />
        </>
      ) : (
        // Voting form
        <div className="space-y-4">
          {poll.questions.map((q) => {
            const isMulti = q.type === 'multiple_choice';
            const selected = answers[q.id] ?? [];
            return (
              <div key={q.id}>
                <p className="text-sm font-semibold text-[var(--color-text,#e2e8f0)] mb-2">{q.text}</p>
                <div className="space-y-2">
                  {q.options.map((opt) => {
                    const checked = selected.includes(opt.id);
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => toggleAnswer(q.id, opt.id, isMulti)}
                        className={`w-full text-left px-3 py-2 rounded-lg border text-sm transition-all duration-150 ${
                          checked
                            ? 'border-[var(--color-primary,#6366f1)] bg-[var(--color-primary,#6366f1)]/20 font-medium text-[var(--color-primary,#a5b4fc)]'
                            : 'border-white/10 text-[var(--color-text-muted,#94a3b8)] hover:border-[var(--color-primary,#6366f1)]/50 hover:text-[var(--color-text,#e2e8f0)]'
                        }`}
                      >
                        {opt.text}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {error && (
            <p className="text-xs text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          <button
            type="button"
            onClick={handleVote}
            disabled={submitting || Object.keys(answers).length === 0}
            className="w-full py-2.5 rounded-lg bg-[var(--color-primary,#6366f1)] text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {submitting && <Spinner />}
            {loyaltyData && poll.completionPoints != null && poll.completionPoints > 0
              ? `Vote — earn +${poll.completionPoints} pts`
              : 'Vote'}
          </button>
        </div>
      )}
    </div>
  );
}
